import React, { ReactNode } from 'react';
import theme from './ProductCard.module.scss';
import { Card, DropdownContextMenu, Badge } from '@ygtechnologies/rubus-ui';
import { BaseProduct, ProductSourceCatalog } from '@/types';
import { getProjectCatalogProductFileUrl } from '@/services/project-catalog';
import { getProfileCatalogProductFileUrl } from '@/services/profile-catalog-products';
import { CatalogProductThumbnails } from '../ProductThumbnails';
import ItemCategoryDisplay from '@/components/item-categories/ItemCategoryDisplay';
import { Trans } from '@lingui/react/macro';
import { classnames, ExternalLink, TextView, Tooltip } from '@ygtechnologies/bygr-frontend-component-library';
import { useIsMobile } from '@/utils/hooks';
import { ProductDetails } from './ProductDetails';
import SmallAttachmentList from '@/components/SmallAttachmentList';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLink, faGripDotsVertical } from '@fortawesome/pro-light-svg-icons';

export interface ProductCardProps {
    isDefault?: boolean;
    isArchived?: boolean;
    dropdownContent?: ReactNode;
    priceElement?: ReactNode;
    bottomElement?: ReactNode;
    dragProps?: object;
    className?: string;
    checked?: boolean;
    onCheckChange?(product: BaseProduct, flag: boolean): void;
}
export interface InternalProductCardProps extends ProductCardProps {
    product: BaseProduct;
    catalog: 'profile' | 'project';
}

export const ProductCard: React.FC<InternalProductCardProps> = ({
    product,
    isDefault,
    isArchived,
    dropdownContent,
    priceElement,
    bottomElement,
    dragProps,
    className,
    checked,
    onCheckChange,
    catalog,
}) => {
    const isMobile = useIsMobile();

    const renderImages = () => (
        <CatalogProductThumbnails
            images={product.images}
            catalog={catalog === 'profile' ? ProductSourceCatalog.Profile : ProductSourceCatalog.Project}
            className={theme.thumbnail}
        />
    );

    return (
        <Card
            isChecked={checked}
            onCheck={onCheckChange ? (flag) => onCheckChange(product, flag) : undefined}
            className={classnames(theme.productCard, { [theme.archived]: isArchived }, className)}>
            {dragProps && (
                <div className={theme.dragHandleContainer}>
                    <FontAwesomeIcon icon={faGripDotsVertical} size="lg" className={theme.dragHandle} {...dragProps} />
                </div>
            )}
            {!isMobile && renderImages()}
            <div className={theme.mainSection}>
                <div className={theme.header}>
                    <div className={theme.left}>
                        <ItemCategoryDisplay className={theme.category} value={product.itemCategoryId} />
                        <strong>{product.name}</strong>
                    </div>

                    <div className={theme.right}>
                        {isDefault && (
                            <Badge variant="info" appearance="subtle">
                                <Trans>Default</Trans>
                            </Badge>
                        )}
                        {isArchived && (
                            <Tooltip
                                content={
                                    <Trans id="archived_pill_tooltip_content">
                                        This product can no longer be selected by home buyers. If already selected by a home
                                        buyer, it will remain selected.
                                    </Trans>
                                }>
                                <Badge>
                                    <Trans>Archived</Trans>
                                </Badge>
                            </Tooltip>
                        )}
                        {/* <Pill variant="default" fill>
                            <Trans>Nordic Breeze</Trans>
                        </Pill> */}
                        {!isMobile && priceElement}
                        {dropdownContent && <DropdownContextMenu>{dropdownContent}</DropdownContextMenu>}
                    </div>
                </div>
                {isMobile && !product.isNone && renderImages()}

                <div className={theme.details}>
                    <div className={theme.detailsSection}>
                        <div className={theme.description}>
                            <TextView numberOfLines={2}>
                                <div>
                                    {product.description || (
                                        <span className="text-missing">
                                            <Trans>No description</Trans>
                                        </span>
                                    )}
                                </div>
                            </TextView>
                        </div>

                        {product.productLink && (
                            <ExternalLink className={theme.productLink} href={product.productLink} target="_blank">
                                <FontAwesomeIcon icon={faExternalLink} />
                                <Trans>Product link</Trans>
                            </ExternalLink>
                        )}
                    </div>
                    <div className={theme.detailsSection}>
                        <ProductDetails product={product} layout="list" />
                    </div>
                </div>
                <SmallAttachmentList
                    className={theme.attachments}
                    urlGenerator={(e) =>
                        catalog === 'profile'
                            ? getProfileCatalogProductFileUrl(e.id)
                            : getProjectCatalogProductFileUrl(e.id)
                    }
                    attachments={product.attachments}
                />

                {bottomElement}

                {isMobile && priceElement && <div className={theme.priceElement}>{priceElement}</div>}
            </div>
        </Card>
    );
};
